import { ProductGroupResponse } from "../common/api/Models/ProductGroupResponse";
import { InventoryDetail } from "../common/CustomerOrder";
import { Product, ProductPrice } from "../common/Product";
import { PurchaseOrder } from "../common/PurchaseOrder";
import { Receive } from "../common/Receive";
import { purchaseOrderStatus } from "./purchaseOrderStatus";
import { EnhancedInvoiceLineItem, EnrichedReceiveLineItem } from "./types";
import {EdiInvoiceLineItem} from "../common/EdiInvoice";

export function isLineItemCancelled(lineItem?: {
	status?: string;
	quantity?: number;
	originalQuantity?: number;
	orderedQuantity?: number;
}): boolean {
	if (!lineItem) {
		return false;
	}
	const orderedQuantity = lineItem?.quantity ?? lineItem?.orderedQuantity ?? 0;
	return (
		lineItem?.status?.toLowerCase() === purchaseOrderStatus.cancelled.toLowerCase() || 
		orderedQuantity < (lineItem?.originalQuantity ?? 0)
	);
}

export function isLineItemOverReceived(enrichedReceiveLineItem: EnrichedReceiveLineItem): boolean {
	// if purchaseOrderReceivedQuantity property exists, then it's validating EDILAM/Manual receive line item
	if (!enrichedReceiveLineItem) {
		return false;
	}
	if (
		enrichedReceiveLineItem.purchaseOrderReceivedQuantity !== undefined &&
		enrichedReceiveLineItem.purchaseOrderReceivedQuantity >= 0
	) {
		const orderedQty = enrichedReceiveLineItem.orderedQuantity ?? 0;
		const receivedQty = enrichedReceiveLineItem.receivedQuantity ?? 0;
		const poReceivedQty = enrichedReceiveLineItem.purchaseOrderReceivedQuantity ?? 0;
		return orderedQty < receivedQty + poReceivedQty;
	}
	const invoicedQty = enrichedReceiveLineItem.invoiced ?? enrichedReceiveLineItem.quantity ?? 0;
	const receivedQty = enrichedReceiveLineItem.received ?? enrichedReceiveLineItem.receivedQuantity ?? 0;

	return invoicedQty < receivedQty;
}

export function quantityOverReceived(enrichedReceiveLineItem: EnrichedReceiveLineItem): number {
	if (!enrichedReceiveLineItem) {
		return 0;
	}
	if (
		enrichedReceiveLineItem.purchaseOrderReceivedQuantity !== undefined &&
		enrichedReceiveLineItem.purchaseOrderReceivedQuantity >= 0
	) {
		const orderedQty = enrichedReceiveLineItem.orderedQuantity ?? 0;
		const receivedQty = enrichedReceiveLineItem.receivedQuantity ?? 0;
		const poReceivedQty = enrichedReceiveLineItem.purchaseOrderReceivedQuantity ?? 0;
		const totalReceived = receivedQty + poReceivedQty;

		return totalReceived > orderedQty ? totalReceived - orderedQty : 0;
	}

	const invoicedQty = enrichedReceiveLineItem.invoiced ?? enrichedReceiveLineItem.quantity ?? 0;
	const receivedQty = enrichedReceiveLineItem.received ?? enrichedReceiveLineItem.receivedQuantity ?? 0;

	return receivedQty > invoicedQty ? receivedQty - invoicedQty : 0;
}

export function updateLineItemCostsAndQuantity(lineItem, quantity, preserveInvoicedQuantity = false) {
	return {
		...lineItem,
		lineTotalCostEx: lineItem.unitCostEx * parseInt(quantity),
		lineTotalCostTax: lineItem.unitCostTax * parseInt(quantity),
		lineTotalCostInc: lineItem.unitCostInc * parseInt(quantity),
		receivedQuantity: parseInt(quantity),
		invoicedQuantity: preserveInvoicedQuantity ? lineItem.invoicedQuantity : parseInt(quantity),
		modifiedTimestamp: new Date(),
	};
}

export function updateLineItemCosts(lineItem, unitIncCost, unitExCost, lineIncCost, lineExCost) {
	return {
		...lineItem,
		unitCostInc: parseFloat(unitIncCost),
		unitCostEx: parseFloat(unitExCost),
		lineTotalCostInc: parseFloat(lineIncCost),
		lineTotalCostEx: parseFloat(lineExCost),
		unitCostTax: unitIncCost - unitExCost,
		lineTotalCostTax: lineIncCost - lineExCost,
	};
}

export function getTotalsFromLineItems(lineItems) {
	let totals = { totalInvoicedQuantity: 0, totalReceivedQuantity: 0, totalCostEx: 0, totalCostInc: 0 };
	lineItems.forEach((li) => {
		totals.totalInvoicedQuantity += li.invoicedQuantity;
		totals.totalReceivedQuantity += li.receivedQuantity;
		totals.totalCostEx += li.unitCostEx * li.receivedQuantity;
		totals.totalCostInc += li.unitCostInc * li.receivedQuantity;
	});
	return totals;
}

// Refactored from Receiving.js
export function updateInvoiceLineItems(
	invoiceLineItems: EdiInvoiceLineItem[],
	products: Product[],
	productGroups: ProductGroupResponse[],
	prices: ProductPrice[],
	purchaseOrder: PurchaseOrder,
	inventory: InventoryDetail[],
	receive?: Receive
): EnhancedInvoiceLineItem[] {
	const updatedInvoiceLineItems: EnhancedInvoiceLineItem[] = [];
	const matchInventory = (sku) => inventory && inventory.find((m) => m.sku === sku);
	const ticketPrices = prices.reduce((tp, item) => {
		tp[item.sku] = item.ticketPrice;
		return tp;
	}, {});
	const promoPrices = prices.reduce((pp, item) => {
		pp[item.sku] = item.promotionPrice;
		return pp;
	}, {});
	const skuListInInvoiceLine = invoiceLineItems.map((il) => il.sku);
	const extraReceiveLineItems = receive?.receiveLineItems.filter((rl) => !skuListInInvoiceLine.includes(rl.sku));
	const extraInvoiceLineItems = extraReceiveLineItems?.map((r) => {
		const product = products.find((product) => product.id === r.sku);
		const productGroup = productGroups.find((productGroup) => productGroup.id === product?.productGroupId);
		const ticketPrice = ticketPrices[r.sku];
		const promoPrice = promoPrices[r.sku];
		const purchaseOrderItem = purchaseOrder.lineItems.find((item) => item.sku === r.sku);
		const matchedInventory = matchInventory(r.sku);
		return {
			model: r.model,
			sku: r.sku,
			barcode: r.barcode,
			description: r.description,
			artist: product?.artist,
			format: product?.format,
			amount: r.unitCostInc,
			unitCost: r.unitCostInc,
			gst: purchaseOrderItem ? purchaseOrderItem.taxPercentage / 100 : 0,
			invoiced: r.invoicedQuantity,
			received: r.receivedQuantity,
			quantityAlreadyReceived: r.quantityAlreadyReceived,
			originalReceived: r.originalReceivedQuantity,
			remainder: r.invoicedQuantity - r.receivedQuantity,
			productGroup: productGroup?.name,
			ticketPrice,
			inventory: matchedInventory,
			purchaseOrderItem: purchaseOrderItem,
			promoPrice,
			isExtraFromReceive: true,
			inboundLocation: r.inboundLocation,
			hasLabel: product?.hasLabel,
		};
	});

	if (extraInvoiceLineItems) {
		updatedInvoiceLineItems.push(...extraInvoiceLineItems);
	}

	for (let i = 0; i < invoiceLineItems.length; i++) {
		const selectedInvoiceLineItem = invoiceLineItems[i];
		const existingPurchaseOrderLineItem = purchaseOrder.lineItems.find(
			(item) => item.sku === selectedInvoiceLineItem.sku
		);
		// how to handle if invoice is incorrect and contains line items not on PO
		const product = products.find((product) => product.id === selectedInvoiceLineItem.sku);
		const productGroup = productGroups.find((productGroup) => productGroup.id === product?.productGroupId);
		const ticketPrice = ticketPrices[selectedInvoiceLineItem.sku];
		const promoPrice = promoPrices[selectedInvoiceLineItem.sku];
		const purchaseOrderItem = purchaseOrder.lineItems.find((item) => item.sku === selectedInvoiceLineItem.sku);

		const matchedInventory = matchInventory(selectedInvoiceLineItem.sku);

		let invoiceLineItem: EnhancedInvoiceLineItem = {
			model: existingPurchaseOrderLineItem?.model,
			sku: existingPurchaseOrderLineItem?.sku ?? selectedInvoiceLineItem.sku,
			barcode: existingPurchaseOrderLineItem?.barcode,
			description: existingPurchaseOrderLineItem?.description,
			artist: product?.artist,
			format: product?.format,
			amount: selectedInvoiceLineItem.unitCostInc,
			unitCost: existingPurchaseOrderLineItem?.unitCostInc,
			gst: selectedInvoiceLineItem.unitCostTax,
			invoiced: selectedInvoiceLineItem.quantity,
			received: 0,
			originalReceived: 0,
			quantityAlreadyReceived: 0,
			remainder: selectedInvoiceLineItem.quantity,
			productGroup: productGroup?.name,
			ticketPrice,
			inventory: matchedInventory,
			purchaseOrderItem: purchaseOrderItem,
			promoPrice,
			isExtraFromReceive: false,
			inboundLocation: "",
			hasLabel: product?.hasLabel,
		};

		if (receive) {
			const existingReceiveLineItem = receive.receiveLineItems.find((item) => item.sku === selectedInvoiceLineItem.sku);
			if (existingReceiveLineItem) {
				invoiceLineItem.received = existingReceiveLineItem.receivedQuantity;
				invoiceLineItem.originalReceived = existingReceiveLineItem.originalReceivedQuantity;
				invoiceLineItem.quantityAlreadyReceived = existingReceiveLineItem.quantityAlreadyReceived;
				invoiceLineItem.remainder = selectedInvoiceLineItem.quantity - existingReceiveLineItem.receivedQuantity;
				invoiceLineItem.inboundLocation = existingReceiveLineItem.inboundLocation;
				if (existingReceiveLineItem.modifiedDate) {
					invoiceLineItem.modifiedTimestamp = new Date(existingReceiveLineItem.modifiedDate);
				}
			}
		}
		updatedInvoiceLineItems.push(invoiceLineItem);
	}
	return updatedInvoiceLineItems;
}
